import { CSSProperties, FC, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import CurrencyInput from "./CurrencyInput";
import NameInput from "./NameInput";
import SequenceInput from "./SequenceInput";
import JourneyStepSelect, { type JourneyStepOption } from "./JourneyStepSelect";
import ConversionRuleButton from "./conversion-rule/ConversionRuleButton";
import { PricesheetService } from "@services/finance/pricesheets/pricesheetService";
import type { JourneyStepDimensionOption } from "@services/finance/pricesheets/commands/cmdListObservedDimensions";
import "./pricesheetDetailPanel.css";

export interface LineItemRow {
  id: number;
  name: string;
  journey_step_id: number;
  journey_path_id: number | null;
  step_price: number;
  estimated_roi: number;
  unit_price: number;
  sequence: number;
  conversion_rule_text: string;
  description?: string;
  is_mandatory?: boolean;
  has_dependency?: boolean;
  is_supplemental?: boolean;
  create_date?: string | null;
  /** FK to the (step, dimension) cell this item prices. Null = step default. */
  journey_step_dimension_id: number | null;
  /** Display name of the JSD; null for the default cell. Not persisted directly. */
  journey_step_dimension_name?: string | null;
}

interface Props {
  item: LineItemRow;
  index: number;
  stepOptions: JourneyStepOption[];
  organizationId?: number;
  contractId?: number | null;
  pricesheetId?: number | null;
  onStepChange: (itemId: number, index: number, stepId: string) => void;
  onNameChange: (itemId: number, name: string) => void;
  onConversionRuleChange?: (itemId: number, ruleText: string) => void;
  onFieldChange?: (
    index: number,
    field: "step_price" | "estimated_roi" | "unit_price",
    raw: string,
  ) => void;
  onAttrChange: (itemId: number, patch: Partial<LineItemRow>) => void;
  /** Every pricing group on this pricesheet, so a step can be moved between them. */
  groupOptions?: { id: number; name: string }[];
  onDelete: (itemId: number) => void;
  /** Called when the user sets or clears the dimension cell on this row. */
  onDimChange?: (
    itemId: number,
    journeyStepDimensionId: number | null,
    journeyStepDimensionName: string | null,
  ) => void;
  /** Another row in this group prices the same (step, dimension) cell — the
   *  server refuses the second one (uq_pricesheetitem_group_step_dimension). */
  isDuplicate?: boolean;
}

const pricesheetService = new PricesheetService();

const detailLabelStyle: CSSProperties = {
  fontSize: "0.7rem",
  fontWeight: 500,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#8a90a2",
  marginBottom: 4,
};

const PriceSheetLineItem: FC<Props> = ({
  item,
  index,
  stepOptions,
  organizationId,
  contractId,
  pricesheetId,
  onStepChange,
  onNameChange,
  onConversionRuleChange,
  onFieldChange,
  onAttrChange,
  onDelete,
  onDimChange,
  groupOptions = [],
  isDuplicate = false,
}) => {
  const hasStep = item.journey_step_id > 0;
  const isCustom = item.is_supplemental ?? false;
  const [expanded, setExpanded] = useState(false);

  const description = item.description ?? "";
  const mandatory = item.is_mandatory ?? false;
  const hasDependency = item.has_dependency ?? false;
  const dateAdded = item.create_date
    ? new Date(item.create_date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

  const [dimPickerOpen, setDimPickerOpen] = useState(false);

  const hasDimension = item.journey_step_dimension_id != null;
  const dimensionLabel = hasDimension
    ? item.journey_step_dimension_name ?? `#${item.journey_step_dimension_id}`
    : null;

  return (
    <div
      className={`jsd-item mb-2${expanded && hasStep ? " jsd-open" : ""}${isCustom ? " jsd-custom" : ""}${isDuplicate ? " jsd-duplicate" : ""}`}
    >
      {isDuplicate && (
        <div className="small text-danger px-3 pt-1" role="alert">
          <i className="bi bi-exclamation-triangle me-1" />
          Another row in this group already prices this step
          {hasDimension ? " and dimension" : ""}. Pick a different step or dimension, or remove one.
        </div>
      )}
      <div className="jsd-grid jsd-row">
        <div className="d-flex justify-content-center">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            onClick={() => setExpanded((v) => !v)}
            disabled={!hasStep}
            aria-label={expanded ? "Collapse step details" : "Expand step details"}
            title="Step details"
            style={{ lineHeight: 1, color: hasStep ? "#adb5bd" : "#dee2e6" }}
          >
            <i className="bi bi-chevron-right jsd-chevron" data-open={expanded} />
          </Button>
        </div>

        <NameInput
          flex={3}
          defaultValue={item.name}
          placeholder="e.g. Enroll Fall 2026"
          onCommit={(val) => onNameChange(item.id, val)}
        />

        <div>
          <JourneyStepSelect
            options={stepOptions}
            value={item.journey_step_id}
            onChange={(stepId) => onStepChange(item.id, index, String(stepId))}
          />
        </div>

        <div>
          {organizationId !== undefined && (
            <ConversionRuleButton
              ruleText={item.conversion_rule_text}
              organizationId={organizationId}
              onChange={(ruleText) => onConversionRuleChange?.(item.id, ruleText)}
            />
          )}
        </div>

        {/* DIMENSION column */}
        <div className="d-flex align-items-center">
          {hasStep && onDimChange && contractId != null && pricesheetId != null ? (
            <button
              type="button"
              onClick={() => setDimPickerOpen(true)}
              style={{
                background: "none",
                border: "none",
                padding: "2px 6px",
                borderRadius: 6,
                cursor: "pointer",
                fontFamily: dimensionLabel ? "monospace" : undefined,
                // The empty state is a plus, so give it enough weight to read as a control
                // rather than a stray character.
                fontSize: dimensionLabel ? "0.8rem" : "1rem",
                lineHeight: 1,
                color: dimensionLabel ? "#1c2030" : "var(--bs-secondary-color, #6c757d)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
              title={dimensionLabel ?? "Set dimension"}
            >
              {/* A dash reads as "nothing here"; a plus says the empty cell is the control.
                  Only on the clickable variant — the read-only one below has nothing to add. */}
              {dimensionLabel ?? "+"}
            </button>
          ) : (
            <span
              className="small"
              style={{ color: "var(--bs-secondary-color, #6c757d)", paddingLeft: 6 }}
              title={dimensionLabel ?? "No dimension"}
            >
              {dimensionLabel ?? "—"}
            </span>
          )}
        </div>

        {/* SEQ column — display order within the group, typed not dragged (MPN-1589) */}
        <div className="d-flex align-items-center">
          <SequenceInput
            defaultValue={item.sequence}
            onCommit={(value) => onAttrChange?.(item.id, { sequence: value })}
          />
        </div>

        <CurrencyInput
          flex={2}
          defaultValue={item.step_price}
          onCommit={(val) => onFieldChange?.(index, "step_price", String(val))}
        />

        <div className="d-flex align-items-center">
          <span className="small" style={{ color: "#8a90a2" }}>
            {dateAdded}
          </span>
        </div>

        <div className="d-flex justify-content-center align-items-center">
          <DeleteIconButton onClick={() => onDelete(item.id)} />
        </div>
      </div>

      {hasStep && (
        <div className={`jsd-collapse${expanded ? " open" : ""}`}>
          <div className="jsd-collapse-inner">
            <div className="jsd-details">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span
                  className="fw-semibold"
                  style={{ fontSize: "0.75rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#6c757d" }}
                >
                  Journey step details
                </span>
                {isCustom && <span className="jsd-custom-badge">Custom</span>}
              </div>

              {/* Which pricing group prices this step. Until MPN-1592 there was no
                  way to change this at all — journey_path_id was set when the step
                  was created and nothing could move it, so a step in the wrong group
                  had to be deleted and rebuilt. That stopped being an option once
                  archiving a step with invoiced conversions became a refusal
                  (MPN-1594), which would have left no way out at all. */}
              {/* Shown when there is somewhere to move to, and always for a step
                  that has no group — that one is the case that matters most, and
                  gating on "more than one group" would have hidden it exactly
                  there, on a sheet with a single group to adopt it. */}
              {groupOptions.length > 0 &&
                (groupOptions.length > 1 || item.journey_path_id == null) && (
                <Form.Group className="mb-3">
                  <Form.Label style={detailLabelStyle}>Pricing group</Form.Label>
                  <Form.Select
                    value={item.journey_path_id ?? ""}
                    onChange={(e) =>
                      onAttrChange(item.id, { journey_path_id: Number(e.target.value) })
                    }
                    style={{ maxWidth: 320 }}
                  >
                    {/* Only present while the step is ungrouped, so the select cannot
                        read as "assigned to the first group" when it is assigned to
                        nothing. Not selectable once a group is chosen: a step with no
                        group prices every launch on the contract. */}
                    {item.journey_path_id == null && (
                      <option value="" disabled>
                        No group — prices every launch on the contract
                      </option>
                    )}
                    {groupOptions.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name || `Group #${g.id}`}
                      </option>
                    ))}
                  </Form.Select>
                  <div className="text-muted" style={{ fontSize: "0.72rem", marginTop: 4 }}>
                    The group decides which launches this step prices — its population.
                  </div>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label style={detailLabelStyle}>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={description}
                  placeholder="Describe this journey step"
                  onChange={(e) => onAttrChange(item.id, { description: e.target.value })}
                />
              </Form.Group>

              <div className="d-flex gap-4 flex-wrap align-items-start">
                <Form.Group>
                  <Form.Label style={detailLabelStyle}>Mandatory step</Form.Label>
                  <div className="d-flex align-items-center gap-2" style={{ minHeight: 38 }}>
                    <Form.Check
                      type="switch"
                      className="jsd-switch"
                      checked={mandatory}
                      onChange={(e) => onAttrChange(item.id, { is_mandatory: e.target.checked })}
                    />
                    <span className="small fw-medium">{mandatory ? "Yes" : "No"}</span>
                  </div>
                </Form.Group>

                <Form.Group>
                  <Form.Label style={detailLabelStyle}>Has dependency</Form.Label>
                  <div className="d-flex align-items-center gap-2" style={{ minHeight: 38 }}>
                    <Form.Check
                      type="switch"
                      className="jsd-switch"
                      checked={hasDependency}
                      onChange={(e) => onAttrChange(item.id, { has_dependency: e.target.checked })}
                    />
                    <span className="small fw-medium">{hasDependency ? "Yes" : "No"}</span>
                  </div>
                </Form.Group>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasStep && dimPickerOpen && onDimChange && contractId != null && pricesheetId != null && (
        <DimensionPickerModal
          contractId={contractId}
          pricesheetId={pricesheetId}
          stepId={item.journey_step_id}
          currentId={item.journey_step_dimension_id}
          onConfirm={(id, name) => {
            onDimChange(item.id, id, name);
            setDimPickerOpen(false);
          }}
          onClose={() => setDimPickerOpen(false)}
        />
      )}
    </div>
  );
};

// ----- Dimension Picker Modal -----
// Opens when the user clicks the DIMENSION cell on a row. Lists the
// JourneyStepDimensions declared on the step; picking one sets the row's
// journey_step_dimension_id FK. Dimensions are declared server-side (seed
// command / MPN-1189) — there is no way to author one here, so an empty list
// shows an empty state rather than free-text entry.

interface DimensionPickerModalProps {
  contractId: number;
  pricesheetId: number;
  stepId: number;
  currentId: number | null;
  onConfirm: (journeyStepDimensionId: number | null, journeyStepDimensionName: string | null) => void;
  onClose: () => void;
}

const DimensionPickerModal: FC<DimensionPickerModalProps> = ({
  contractId,
  pricesheetId,
  stepId,
  currentId,
  onConfirm,
  onClose,
}) => {
  const [options, setOptions] = useState<JourneyStepDimensionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(currentId);

  useEffect(() => {
    pricesheetService
      .listObservedDimensions(contractId, pricesheetId, stepId)
      .then(setOptions)
      .finally(() => setLoading(false));
   
  }, []);

  const canConfirm = selectedId !== null && selectedId !== currentId;

  return (
    <Modal show onHide={onClose} size="sm">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem" }}>Set Dimension</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-2">
            <Spinner animation="border" size="sm" />
          </div>
        ) : options.length === 0 ? (
          <p className="small text-muted mb-0">
            No dimensions declared for this step yet. Dimensions (e.g. terms) are
            set up server-side before they can be priced here.
          </p>
        ) : (
          <>
            <Form.Label className="small fw-semibold">Step dimensions</Form.Label>
            <div className="mb-2">
              {options.map((d) => {
                const isSelected = selectedId === d.id;
                return (
                  <div
                    key={d.id}
                    className="p-2 mb-1 rounded border small"
                    style={{
                      cursor: "pointer",
                      background: isSelected ? "#eef2ff" : "#f9fafc",
                      borderColor: isSelected ? "#845adf" : "#dee2e6",
                    }}
                    onClick={() => setSelectedId(d.id)}
                  >
                    {d.name}
                    <span className="text-muted ms-1" style={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                      {d.code}
                    </span>
                  </div>
                );
              })}
            </div>
            {currentId !== null && (
              <Button
                variant="link"
                size="sm"
                className="p-0 text-danger text-decoration-none"
                onClick={() => onConfirm(null, null)}
              >
                Clear dimension (use step default)
              </Button>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!canConfirm}
          onClick={() => {
            const chosen = options.find((d) => d.id === selectedId);
            if (chosen) onConfirm(chosen.id, chosen.name);
          }}
        >
          Set
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const DeleteIconButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <Button
      variant="link"
      className="d-flex align-items-center justify-content-center"
      style={{
        width: 38,
        height: 38,
        padding: 0,
        textDecoration: "none",
        lineHeight: 1,
        color: hover ? "var(--bs-danger)" : "var(--bs-secondary-color, #6c757d)",
        transform: hover ? "scale(1.2)" : "scale(1)",
        transition: "transform 150ms ease, color 150ms ease",
      }}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="Remove line item"
    >
      <i className="bi bi-x-lg" />
    </Button>
  );
};

export default PriceSheetLineItem;
